import { useEffect, useMemo, useState } from "react";
import type { PaletteSet } from "@picnic-field/renderer/appearance";
import type { FieldApi } from "../lib/api/fieldApi";

/** appearance(모양 모름) → 팔레트 이름. 없으면 null */
export function paletteNameOf(appearance: unknown): string | null {
  const p = (appearance as { palette?: unknown } | null | undefined)?.palette;
  return typeof p === "string" && p ? p : null;
}

const EMPTY: PaletteSet = new Map();

/**
 * 여러 작품을 한 장면에 그리는 화면(룸 · 칠판 · 놀이 설정)의 팔레트 묶음 — 작품마다 제 appearance.palette 이름으로 칠한다
 * (2026-09-30 팔레트 버전 전환 · Campus 현행 smartblock-v1 · 옛 작품 brick-v1). 이름 목록이 바뀔 때만 다시 받는다.
 * 받기 전 · 못 받은 이름은 묶음에 없다(그 작품은 원래 색 — 오류 아님). 새 이름을 받는 동안은 지난 묶음을 그대로 둔다
 * (이름으로 고르므로 지난 묶음에 있는 이름은 여전히 맞는 색).
 */
export function usePaletteSet(
  api: Pick<FieldApi, "palettes"> | null,
  appearances: readonly unknown[] | null | undefined,
): PaletteSet {
  const key = useMemo(
    () =>
      [...new Set((appearances ?? []).map(paletteNameOf).filter((n): n is string => !!n))]
        .sort()
        .join("\n"),
    [appearances],
  );
  const [set, setSet] = useState<PaletteSet>(EMPTY);
  useEffect(() => {
    if (!api || !key) return;
    let alive = true;
    api
      .palettes(key.split("\n"))
      .then((p) => alive && setSet(p))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [api, key]);
  return set;
}
